import { useMutation, useQueryClient, UseMutationResult } from '@tanstack/react-query';
import { toast } from 'sonner';

/**
 * Resposta estàndard de l'API
 */
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

/**
 * Configuració del servei per l'entitat
 */
export interface EntityService<T> {
  create?: (data: Partial<T>) => Promise<ApiResponse<T>>;
  update?: (id: string, data: Partial<T>) => Promise<ApiResponse<T>>;
  /** El hook només en llegeix `success` i `message`; cada servei hi torna el `data` que vol. */
  delete?: (id: string) => Promise<ApiResponse<unknown>>;
}

/**
 * Configuració de missatges personalitzats
 */
export interface EntityMessages {
  createSuccess?: string;
  updateSuccess?: string;
  deleteSuccess?: string;
  createError?: string;
  updateError?: string;
  deleteError?: string;
}

/**
 * Configuració completa per useEntityMutation
 */
export interface EntityMutationConfig<T> {
  /** Nom de l'entitat (per defecte en missatges) */
  entity: string;
  /** Servei amb mètodes CRUD */
  service: EntityService<T>;
  /** Query keys a invalidar després de mutacions */
  queryKey: string[];
  /** Query keys addicionals a invalidar (opcional) */
  additionalQueryKeys?: string[][];
  /** Missatges personalitzats (opcional) */
  messages?: EntityMessages;
  /** Callback després de crear (opcional) */
  onCreateSuccess?: (data: T) => void;
  /** Callback després d'actualitzar (opcional) */
  onUpdateSuccess?: (data: T) => void;
  /** Callback després d'eliminar (opcional) */
  onDeleteSuccess?: () => void;
}

/**
 * Retorn del hook amb les 3 mutacions
 */
export interface EntityMutationReturn<T> {
  createMutation: UseMutationResult<ApiResponse<T>, Error, Partial<T>>;
  updateMutation: UseMutationResult<ApiResponse<T>, Error, { id: string; data: Partial<T> }>;
  deleteMutation: UseMutationResult<ApiResponse<unknown>, Error, string>;
}

/**
 * Hook genèric per gestionar mutacions CRUD d'entitats
 * 
 * Elimina duplicació de codi entre hooks create/update/delete
 * 
 * @example
 * ```ts
 * const { createMutation, updateMutation, deleteMutation } = useEntityMutation<Client>({
 *   entity: 'client',
 *   service: ClientsService,
 *   queryKey: ['clients'],
 *   messages: {
 *     createSuccess: 'Client creat correctament',
 *     updateSuccess: 'Client actualitzat correctament',
 *   },
 * });
 * 
 * // Crear
 * createMutation.mutate({ nom: 'Joan', cognoms: 'Garcia' });
 * 
 * // Actualitzar
 * updateMutation.mutate({ id: '123', data: { nom: 'Pere' } });
 * 
 * // Eliminar
 * deleteMutation.mutate('123');
 * ```
 */
export function useEntityMutation<T>(config: EntityMutationConfig<T>): EntityMutationReturn<T> {
  const queryClient = useQueryClient();

  // Mutation CREATE
  const createMutation = useMutation({
    mutationFn: async (data: Partial<T>) => {
      if (!config.service.create) {
        throw new Error(`Create method not implemented for ${config.entity}`);
      }
      const response = await config.service.create(data);
      if (!response.success) {
        throw new Error(response.message || `Error en crear ${config.entity}`);
      }
      return response;
    },
    onSuccess: (response) => {
      // Invalidar query keys
      queryClient.invalidateQueries({ queryKey: config.queryKey });
      if (config.additionalQueryKeys) {
        config.additionalQueryKeys.forEach((key) => {
          queryClient.invalidateQueries({ queryKey: key });
        });
      }

      // Toast success
      const message = config.messages?.createSuccess || `${config.entity} creat correctament`;
      toast.success(message);

      // Callback opcional
      if (config.onCreateSuccess && response.data) {
        config.onCreateSuccess(response.data);
      }
    },
    onError: (error: any) => {
      console.error(`Error creating ${config.entity}:`, error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        config.messages?.createError ||
        `Error en crear ${config.entity}`;
      toast.error(errorMessage);
    },
  });

  // Mutation UPDATE
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<T> }) => {
      if (!config.service.update) {
        throw new Error(`Update method not implemented for ${config.entity}`);
      }
      const response = await config.service.update(id, data);
      if (!response.success) {
        throw new Error(response.message || `Error en actualitzar ${config.entity}`);
      }
      return response;
    },
    onSuccess: (response) => {
      // Invalidar query keys
      queryClient.invalidateQueries({ queryKey: config.queryKey });
      if (config.additionalQueryKeys) {
        config.additionalQueryKeys.forEach((key) => {
          queryClient.invalidateQueries({ queryKey: key });
        });
      }

      // Toast success
      const message = config.messages?.updateSuccess || `${config.entity} actualitzat correctament`;
      toast.success(message);

      // Callback opcional
      if (config.onUpdateSuccess && response.data) {
        config.onUpdateSuccess(response.data);
      }
    },
    onError: (error: any) => {
      console.error(`Error updating ${config.entity}:`, error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        config.messages?.updateError ||
        `Error en actualitzar ${config.entity}`;
      toast.error(errorMessage);
    },
  });

  // Mutation DELETE
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!config.service.delete) {
        throw new Error(`Delete method not implemented for ${config.entity}`);
      }
      const response = await config.service.delete(id);
      if (!response.success) {
        throw new Error(response.message || `Error en eliminar ${config.entity}`);
      }
      return response;
    },
    onSuccess: () => {
      // Invalidar query keys
      queryClient.invalidateQueries({ queryKey: config.queryKey });
      if (config.additionalQueryKeys) {
        config.additionalQueryKeys.forEach((key) => {
          queryClient.invalidateQueries({ queryKey: key });
        });
      }

      // Toast success
      const message = config.messages?.deleteSuccess || `${config.entity} eliminat correctament`;
      toast.success(message);

      // Callback opcional
      if (config.onDeleteSuccess) {
        config.onDeleteSuccess();
      }
    },
    onError: (error: any) => {
      console.error(`Error deleting ${config.entity}:`, error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        config.messages?.deleteError ||
        `Error en eliminar ${config.entity}`;
      toast.error(errorMessage);
    },
  });

  return {
    createMutation,
    updateMutation,
    deleteMutation,
  };
}
